import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Search, ShieldCheck, Building2, KeyRound, Plane, Hammer, MapPin, ChevronDown, BookOpen, Quote } from "lucide-react";
import { useLang } from "@/context";
import { T, linkTo } from "@/i18n";
import { ZONES } from "@/data/zones";
import { SERVICES } from "@/data/services";
import { ARTICLES } from "@/data/articles";
import { STEPS, WHY, FAQS, TESTIMONIALS } from "@/data/misc";
import { MaskLine, Reveal, Marquee } from "@/components/motion";
import { PropertyCard } from "@/components/PropertyCard";
import { LeadForm } from "@/components/LeadForm";
import { SEO } from "@/components/SEO";
import api from "@/api";

const SERVICE_ICONS = { ShieldCheck, Building2, KeyRound, Plane, Hammer };

const HERO_IMG = "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=2000&q=80&auto=format&fit=crop";
const CTA_IMG = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1800&q=75&auto=format&fit=crop";
const WHY_IMG = "https://images.unsplash.com/photo-1535941339077-2dd1c7963098?w=1200&q=75&auto=format&fit=crop";
const RESIDENCY_IMG = "https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1400&q=75&auto=format&fit=crop";

function HeroSearch() {
  const { lang } = useLang();
  const t = T[lang].hero;
  const navigate = useNavigate();
  const [type, setType] = useState("");
  const [zone, setZone] = useState("");
  const [budget, setBudget] = useState("");
  const budgets = [[null, 20000], [20000, 50000], [50000, 150000], [150000, null]];
  const go = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (type) p.set("type", type);
    if (zone) p.set("zone", zone);
    if (budget !== "") {
      const [min, max] = budgets[Number(budget)];
      if (min) p.set("min_price", min);
      if (max) p.set("max_price", max);
    }
    navigate(`${linkTo(lang, "properties")}?${p.toString()}`);
  };
  return (
    <motion.form
      onSubmit={go}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-20 -mt-10 mx-auto max-w-5xl rounded-3xl bg-white/90 backdrop-blur-2xl shadow-2xl border border-[#0E4D5C]/15 p-5 md:p-7"
      data-testid="hero-search-form"
    >
      <p className="eyebrow mb-4">{t.searchTitle}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label-site">{t.typeLabel}</label>
          <select className="input-site" value={type} onChange={(e) => setType(e.target.value)} data-testid="hero-type-select">
            <option value="">{t.typeAny}</option>
            {Object.entries(T[lang].types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="label-site">{t.zoneLabel}</label>
          <select className="input-site" value={zone} onChange={(e) => setZone(e.target.value)} data-testid="hero-zone-select">
            <option value="">{t.zoneAny}</option>
            {ZONES.map((z) => <option key={z.slug} value={z.slug}>{z.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-site">{t.budgetLabel}</label>
          <select className="input-site" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="hero-budget-select">
            <option value="">{t.budgetAny}</option>
            {t.budgetOptions.map((b, i) => <option key={b} value={i}>{b}</option>)}
          </select>
        </div>
      </div>
      <button type="submit" className="btn-primary w-full mt-5" data-testid="hero-search-submit">
        <Search size={17} /> {t.submit}
      </button>
    </motion.form>
  );
}

const MAP_IMG = "https://commons.wikimedia.org/wiki/Special:FilePath/Madagascar_sat.png?width=900";
const ZONE_GEO = {
  "nosy-be": { x: 62, y: 15 }, "sainte-marie": { x: 87, y: 31 },
  "diego-suarez": { x: 76, y: 8 }, "antananarivo": { x: 59, y: 50 },
  "tamatave": { x: 83, y: 45 }, "tulear": { x: 12, y: 80 },
  "fort-dauphin": { x: 61, y: 87 }, "morondava": { x: 20, y: 59 },
  "altopiani": { x: 50, y: 51 },
};

function MadagascarMap() {
  const { lang } = useLang();
  const [active, setActive] = useState(null);
  const navigate = useNavigate();
  return (
    <div className="relative mx-auto max-w-md" data-testid="madagascar-map">
      <div className="relative overflow-hidden rounded-3xl border border-[#F6EFE4]/15 shadow-2xl">
        <img src={MAP_IMG} alt={lang === "it" ? "Immagine satellitare del Madagascar e di Nosy Be" : "Satellite image of Madagascar and Nosy Be"}
          className="w-full h-auto block" loading="lazy" />
        {ZONES.map((z) => {
          const p = ZONE_GEO[z.slug];
          return (
            <button key={z.slug}
              onClick={() => navigate(linkTo(lang, "zone", z.slug))}
              onMouseEnter={() => setActive(z.slug)} onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(z.slug)} onBlur={() => setActive(null)}
              aria-label={z.name} data-testid={`map-pin-${z.slug}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
              style={{ left: `${p.x}%`, top: `${p.y}%` }}>
              <span className={`block rounded-full border-2 border-[#0E4D5C] bg-[#F6EFE4] transition-all duration-300 ${active === z.slug ? "h-5 w-5 !bg-[#E8B64C] shadow-[0_0_18px_rgba(232,182,76,0.9)]" : "h-3.5 w-3.5 shadow-[0_0_10px_rgba(246,239,228,0.6)]"}`} />
              {active === z.slug && (
                <span className="absolute left-1/2 -translate-x-1/2 -top-9 whitespace-nowrap rounded-full bg-[#F6EFE4] px-3 py-1 text-xs font-semibold text-[#0E4D5C] shadow-lg">
                  {z.name}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Home() {
  const { lang } = useLang();
  const t = T[lang];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const [featured, setFeatured] = useState([]);
  const [tab, setTab] = useState("all");
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/listings", { params: { featured: "true" } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const filtered = tab === "all" ? featured : featured.filter((l) => l.type === tab);
  const tabs = ["all", "beachfront", "land", "villa", "farmland"];

  return (
    <div data-testid="home-page">
      <SEO title={t.seo.homeTitle} description={t.seo.homeDesc} lang={lang} pathKey="home" />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden grain" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={HERO_IMG} alt={lang === "it" ? "Viale dei baobab al tramonto, Madagascar" : "Avenue of the Baobabs at sunset, Madagascar"}
            className="h-full w-full object-cover scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F2328]/85 via-[#0E4D5C]/35 to-[#1F2328]/20" />
        </motion.div>
        <div className="container-site relative z-10 pb-28 pt-40 w-full">
          <h1 className="font-serif font-light text-[#F6EFE4] text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            <MaskLine delay={0.15}>{t.hero.line1}</MaskLine>
            <MaskLine delay={0.35}><span className="italic text-[#E8B64C]">{t.hero.line2}</span></MaskLine>
            <MaskLine delay={0.55}>{t.hero.line3}</MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-6 max-w-2xl text-base sm:text-lg text-[#F6EFE4]/85 leading-relaxed">
            {t.hero.subtitle}
          </motion.p>
        </div>
        <motion.div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[#F6EFE4]/70 flex flex-col items-center gap-1 text-xs uppercase tracking-[0.25em]"
          animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4 }}>
          {t.hero.scroll} <ChevronDown size={16} />
        </motion.div>
      </section>

      <HeroSearch />
      <div className="mt-16"><Marquee text={t.home.marquee} /></div>

      {/* TRUST NUMBERS */}
      <section className="container-site py-20 md:py-28" data-testid="trust-section">
        <Reveal><p className="eyebrow">{t.trust.title}</p></Reveal>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {t.trust.items.map((it, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <p className="font-serif text-5xl lg:text-6xl text-[#B5472B]" data-testid={`trust-value-${i}`}>{it.value}</p>
              <p className="mt-2 text-sm text-[#1F2328]/65 leading-snug">{it.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-white/60 py-20 md:py-28" data-testid="featured-section">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">{t.nav.properties}</p>
            <h2 className="h2-serif mt-3 max-w-2xl">{t.home.featuredTitle}</h2>
            <p className="mt-4 max-w-2xl text-[#1F2328]/70">{t.home.featuredSub}</p>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="featured-tabs">
            {tabs.map((k) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`featured-tab-${k}`}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-200 ${tab === k ? "bg-[#0E4D5C] text-[#F6EFE4]" : "bg-[#0E4D5C]/5 text-[#0E4D5C] hover:bg-[#0E4D5C]/15"}`}>
                {k === "all" ? t.common.all : t.types[k]}
              </button>
            ))}
          </div>
          <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filtered.slice(0, 6).map((item, i) => (
              <Reveal key={item.id || item.ref_code} delay={i * 0.08}>
                <PropertyCard item={item} />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to={linkTo(lang, "properties")} className="btn-outline" data-testid="featured-view-all">{t.home.viewAll} <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-[#0E4D5C] text-[#F6EFE4] py-20 md:py-28 grain relative overflow-hidden" data-testid="map-section">
        <div className="container-site grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="eyebrow !text-[#E8B64C]">{t.nav.zones}</p>
            <h2 className="h2-serif !text-[#F6EFE4] mt-3">{t.home.mapTitle}</h2>
            <p className="mt-5 text-[#F6EFE4]/75 leading-relaxed max-w-md">{t.home.mapSub}</p>
            <Link to={linkTo(lang, "zones")} className="btn-primary mt-8" data-testid="map-cta">{t.home.mapCta} <ArrowRight size={16} /></Link>
          </Reveal>
          <Reveal delay={0.15}><MadagascarMap /></Reveal>
        </div>
      </section>

      {/* STEPS */}
      <section className="container-site py-20 md:py-28" data-testid="steps-section">
        <Reveal>
          <p className="eyebrow">{t.nav.howItWorks}</p>
          <h2 className="h2-serif mt-3">{t.home.stepsTitle}</h2>
          <p className="mt-4 text-[#1F2328]/70 max-w-xl">{t.home.stepsSub}</p>
        </Reveal>
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS[lang].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1} className="relative">
              <p className="font-serif text-6xl text-[#B5472B]/25">{s.n}</p>
              <h3 className="font-serif text-xl font-semibold mt-3 text-[#1F2328]">{s.title}</h3>
              <p className="mt-2 text-sm text-[#1F2328]/65 leading-relaxed">{s.text}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10"><Link to={linkTo(lang, "howItWorks")} className="btn-outline" data-testid="steps-cta">{t.nav.howItWorks} <ArrowRight size={16} /></Link></Reveal>
      </section>

      {/* SERVICES */}
      <section className="bg-white/60 py-20 md:py-28" data-testid="services-section">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">{t.nav.services}</p>
            <h2 className="h2-serif mt-3">{t.home.servicesTitle}</h2>
            <p className="mt-4 text-[#1F2328]/70 max-w-2xl">{t.home.servicesSub}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.icon];
              return (
                <Reveal key={s.slug} delay={i * 0.07}>
                  <Link to={linkTo(lang, "service", s.slug)} className="card-site block p-7 h-full group" data-testid={`service-card-${s.slug}`}>
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#B5472B]/10 text-[#B5472B]"><Icon size={22} strokeWidth={1.7} /></span>
                    <h3 className="font-serif text-xl font-semibold mt-4 text-[#1F2328] group-hover:text-[#B5472B] transition-colors">{s[lang].name}</h3>
                    <p className="mt-2 text-sm text-[#1F2328]/65 leading-relaxed">{s[lang].short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#0E4D5C]">{t.common.readMore} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
                  </Link>
                </Reveal>
              );
            })}
            <Reveal delay={0.35}>
              <Link to={linkTo(lang, "farmland")} className="card-site block p-7 h-full group bg-[#2FA3A0]/10" data-testid="service-card-farmland">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#2FA3A0]/15 text-[#2FA3A0]"><MapPin size={22} strokeWidth={1.7} /></span>
                <h3 className="font-serif text-xl font-semibold mt-4 text-[#1F2328] group-hover:text-[#B5472B] transition-colors">{t.nav.farmland}</h3>
                <p className="mt-2 text-sm text-[#1F2328]/65 leading-relaxed">{t.farmlandPage.sub.split(".")[0]}.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#0E4D5C]">{t.common.readMore} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RESIDENCY BAND */}
      <section className="container-site py-20 md:py-28" data-testid="residency-section">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative">
            <div className="absolute -inset-3 border border-[#B5472B]/30 rounded-3xl translate-x-4 translate-y-4" aria-hidden="true" />
            <img src={RESIDENCY_IMG} alt={lang === "it" ? "Paesaggio tropicale del Madagascar" : "Tropical landscape of Madagascar"} className="relative rounded-3xl w-full h-[420px] object-cover" loading="lazy" />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow">{lang === "it" ? "Programma residenza" : "Residency programme"}</p>
            <h2 className="h2-serif mt-3">{t.home.residencyTitle}</h2>
            <p className="mt-5 text-[#1F2328]/70 leading-relaxed">{t.home.residencyText}</p>
            <Link to={linkTo(lang, "service", "residenza")} className="btn-primary mt-7" data-testid="residency-cta">{t.home.residencyCta} <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      {/* WHY MANIFESTO */}
      <section className="bg-[#1F2328] text-[#F6EFE4] py-20 md:py-28 grain relative" data-testid="why-section">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow !text-[#E8B64C]">{t.home.whyTitle}</p>
            <h2 className="h2-serif !text-[#F6EFE4] mt-3 max-w-2xl">{t.home.whySub}</h2>
          </Reveal>
          <div className="mt-14 grid gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <img src={WHY_IMG} alt={lang === "it" ? "Baobab nel paesaggio malgascio" : "Baobab in the Malagasy landscape"} className="rounded-3xl w-full h-full min-h-[320px] object-cover" loading="lazy" />
            </Reveal>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-10 gap-y-12">
              {WHY[lang].map((w, i) => (
                <Reveal key={w.n} delay={i * 0.08}>
                  <p className="font-serif text-4xl text-[#E8B64C]">{w.n}</p>
                  <h3 className="font-serif text-xl font-semibold mt-3">{w.title}</h3>
                  <p className="mt-2 text-sm text-[#F6EFE4]/65 leading-relaxed">{w.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ZONES GRID */}
      <section className="container-site py-20 md:py-28" data-testid="zones-section">
        <Reveal>
          <p className="eyebrow">{t.nav.zones}</p>
          <h2 className="h2-serif mt-3">{t.home.zonesTitle}</h2>
          <p className="mt-4 text-[#1F2328]/70 max-w-2xl">{t.home.zonesSub}</p>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ZONES.map((z, i) => (
            <Reveal key={z.slug} delay={(i % 3) * 0.08}>
              <Link to={linkTo(lang, "zone", z.slug)} className="group relative block overflow-hidden rounded-3xl h-64" data-testid={`zone-card-${z.slug}`}>
                <img src={z.img} alt={`${z.name} — ${z[lang].tagline}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F2328]/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-serif text-2xl text-[#F6EFE4]">{z.name}</h3>
                  <p className="text-sm text-[#F6EFE4]/75 mt-1">{z[lang].tagline}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white/60 py-20 md:py-28" data-testid="testimonials-section">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">{t.nav.testimonials}</p>
            <h2 className="h2-serif mt-3">{t.home.testimonialsTitle}</h2>
            <p className="mt-4 text-sm text-[#8A8F96] max-w-2xl">{t.home.testimonialsNote}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((tm, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <figure className="card-site p-7 h-full" data-testid={`testimonial-${i}`}>
                  <Quote size={22} className="text-[#B5472B]/40" />
                  <blockquote className="mt-4 text-sm text-[#1F2328]/75 leading-relaxed">{tm[lang]}</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0E4D5C] text-[#F6EFE4] text-sm font-semibold">{tm.initials}</span>
                    <span className="text-xs text-[#8A8F96]">{tm.meta[lang]}</span>
                  </figcaption>
                  <span className="mt-4 inline-block bg-[#E8B64C]/20 text-[#B5472B] text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1">{t.common.example}</span>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FREE GUIDE */}
      <section className="container-site py-20 md:py-28" data-testid="guide-section">
        <div className="rounded-3xl bg-[#B5472B] text-[#F6EFE4] p-8 md:p-14 grid lg:grid-cols-2 gap-10 items-center grain relative overflow-hidden">
          <Reveal>
            <p className="eyebrow !text-[#E8B64C]">{lang === "it" ? "Download gratuito" : "Free download"}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">{t.home.guideTitle}</h2>
            <p className="mt-4 text-[#F6EFE4]/85 leading-relaxed max-w-md">{t.home.guideSub}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-[#F6EFE4] text-[#1F2328] rounded-2xl p-6 md:p-8">
              <LeadForm type="guide" compact testIdPrefix="home-guide-form" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="container-site py-20 md:py-28" data-testid="articles-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow">{t.nav.guides}</p>
              <h2 className="h2-serif mt-3">{t.home.articlesTitle}</h2>
              <p className="mt-3 text-[#1F2328]/70 max-w-xl">{t.home.articlesSub}</p>
            </div>
            <Link to={linkTo(lang, "guides")} className="btn-outline" data-testid="articles-view-all">{t.home.allArticles} <ArrowRight size={15} /></Link>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {ARTICLES.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.08}>
              <Link to={linkTo(lang, "article", a.slug)} className="group block" data-testid={`article-card-${a.slug}`}>
                <div className="overflow-hidden rounded-2xl">
                  <img src={a.img} alt={a[lang].title} className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#B5472B]">{a[lang].category}</p>
                <h3 className="font-serif text-xl font-semibold mt-2 leading-snug text-[#1F2328] group-hover:text-[#B5472B] transition-colors">{a[lang].title}</h3>
                <p className="mt-2 text-sm text-[#1F2328]/60 leading-relaxed line-clamp-2">{a[lang].excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white/60 py-20 md:py-28" data-testid="faq-section">
        <div className="container-site max-w-4xl">
          <Reveal>
            <p className="eyebrow">{t.nav.faq}</p>
            <h2 className="h2-serif mt-3">{t.home.faqTitle}</h2>
            <p className="mt-3 text-[#1F2328]/70">{t.home.faqSub}</p>
          </Reveal>
          <div className="mt-10 divide-y divide-[#0E4D5C]/10 border-y border-[#0E4D5C]/10">
            {FAQS.map((f, i) => (
              <div key={i}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-question-${i}`}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left font-serif text-lg text-[#1F2328] hover:text-[#B5472B] transition-colors"
                  aria-expanded={openFaq === i}>
                  {f[lang].q}
                  <ChevronDown size={18} className={`shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180 text-[#B5472B]" : ""}`} />
                </button>
                {openFaq === i && <p className="pb-6 text-sm text-[#1F2328]/70 leading-relaxed max-w-3xl" data-testid={`faq-answer-${i}`}>{f[lang].a}</p>}
              </div>
            ))}
          </div>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f[lang].q, acceptedAnswer: { "@type": "Answer", text: f[lang].a } })),
          })}} />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden grain" data-testid="final-cta">
        <img src={CTA_IMG} alt={lang === "it" ? "Laguna turchese e spiaggia bianca" : "Turquoise lagoon and white beach"} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-[#0E4D5C]/70" />
        <div className="container-site relative z-10 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-[#F6EFE4] leading-tight max-w-3xl mx-auto">{t.home.ctaTitle}</h2>
            <p className="mt-5 text-[#F6EFE4]/85 max-w-xl mx-auto">{t.home.ctaSub}</p>
            <Link to={linkTo(lang, "contact")} className="btn-primary mt-9 !px-9 !py-4 !text-base" data-testid="final-cta-button">
              <BookOpen size={18} /> {t.home.ctaButton}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
